import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Select from 'react-select';
import {
  FaCalendarAlt,
  FaRegCalendarAlt,
  FaShieldAlt,
  FaFileAlt,
  FaBriefcase,
  FaDollarSign,
  FaInfoCircle,
} from 'react-icons/fa';
import WebsiteTopBar from '../../components/WebsiteTopBar';
import {
  LOGISTICS_INDUSTRIES_SERVED,
  LOGISTICS_TRANSPORT_MODES,
} from '../../constants/logisticsProfileFormOptions';
import {
  getInitialGenericRfqForm,
  emptyPackageRow,
  emptyDocumentRow,
  generateRfqReferenceNumber,
  getTodayRfqDateString,
  CARGO_WEIGHT_UNIT_OPTIONS,
  CARGO_VOLUME_UNIT_OPTIONS,
  VALUE_CURRENCY_OPTIONS,
  ADDITIONAL_SERVICE_OPTIONS,
  INCOTERM_OPTIONS,
  getShipmentTypesForTransportMode,
  COMMODITY_TYPE_OPTIONS,
  QUOTE_VALIDITY_PERIOD_OPTIONS,
  COMMERCIAL_SHIPMENT_FREQUENCY_OPTIONS,
  CONTRACT_DURATION_OPTIONS,
  legacyPreferredPaymentTermsObjectToLabels,
} from './logisticsGenericRfqFormState';
import { useLocationPicker } from '../../hooks/useLocationPicker';
import { useCurrencyMaster } from '../../hooks/useCurrencyMaster';
import CommonService from '../../services/common.service';
import {
  fetchBuyerRefCompanyFieldsForRfqPrefill,
  mergeBuyerPrefillIntoForm,
  strTrim,
} from './logisticsRfqBuyerPrefill';
import { usePaymentTermsMaster } from '../../hooks/usePaymentTermsMaster';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../assets/styles/website-top-bar.scss';
import '../../assets/styles/logistics_generic_rfq.scss';
import { saveLogisticsRfqPendingSubmit } from '../../utils/logisticsRfqPendingSubmit';
import { getEffectiveP2pLaunchContext } from '../../utils/p2pRfqLinkAfterCreate';
import { submitP2pLogisticsRfq } from '../../utils/p2pLogisticsRfqSubmit';

const DRAFT_PREFIX = 'lgrq-generic-rfq-draft';

const TIMELINE_URGENCY_CARDS = [
  { value: 'Standard', title: 'Standard', desc: '15-30 days' },
  { value: 'Express', title: 'Express', desc: '7-14 days' },
  { value: 'Urgent', title: 'Urgent', desc: '1-7 days' },
];

/** Carrier rating dropdown options (legacy; carrier section UI uses text fields — keep if old markup still references this). */
const CARRIER_RATING_OPTIONS = ['', '5', '4+', '3+', 'Any'];

function draftStorageKey(companyId) {
  const id = companyId != null && Number.isFinite(Number(companyId)) ? String(companyId) : 'none';
  return `${DRAFT_PREFIX}-${id}`;
}

/** Human-readable range for API / JSON (keeps compatibility with `pickupTimeWindow` string field). */
function formatTimeRangeForPayload(from, to) {
  const f = strTrim(from);
  const t = strTrim(to);
  if (f && t) return `${f} – ${t}`;
  if (f) return f;
  if (t) return t;
  return '';
}

function serializeFormForDraft(form) {
  const { documents, ...rest } = form;
  return {
    ...rest,
    rfqStatus: 'draft',
    draftSavedAt: new Date().toISOString(),
    documents: (documents || []).map((d) => ({
      id: d.id,
      fileName: d.fileName,
      sizeLabel: d.sizeLabel,
    })),
  };
}

function coerceBoolish(v) {
  if (v === true || v === 'true' || v === 1 || v === '1') return true;
  if (typeof v === 'string' && v.trim() !== '') return true;
  return false;
}

function migrateLegacyServiceFields(merged, raw) {
  merged.cargoInsuranceRequired = Boolean(merged.cargoInsuranceRequired);
  merged.customsBrokerNeeded = Boolean(merged.customsBrokerNeeded);
  merged.customsClearanceSupport = Boolean(merged.customsClearanceSupport);
  merged.loadingEquipmentRequired = coerceBoolish(merged.loadingEquipmentRequired);
  merged.unloadingEquipmentRequired = coerceBoolish(merged.unloadingEquipmentRequired);
  merged.appointmentDockRequired = Boolean(merged.appointmentDockRequired);
  merged.professionalPackagingRequired = Boolean(merged.professionalPackagingRequired);
  merged.cargoPalletized = Boolean(merged.cargoPalletized);

  const svc =
    merged.additionalServices && typeof merged.additionalServices === 'object'
      ? { ...merged.additionalServices }
      : {};
  if (svc.insurance) {
    merged.cargoInsuranceRequired = merged.cargoInsuranceRequired || Boolean(svc.insurance);
    delete svc.insurance;
  }
  if (svc.customs_brokerage) {
    merged.customsBrokerNeeded = merged.customsBrokerNeeded || Boolean(svc.customs_brokerage);
    delete svc.customs_brokerage;
  }
  if (svc.warehousing && !svc.warehouse_storage) svc.warehouse_storage = true;
  if (svc.last_mile && !svc.door_to_door_delivery) svc.door_to_door_delivery = true;
  if (svc.packaging_crating && !svc.packaging_service) svc.packaging_service = true;
  if (raw && raw.doorToDoorService && !svc.door_to_door_delivery) svc.door_to_door_delivery = true;
  if (svc.appointment_delivery && !merged.appointmentDockRequired) merged.appointmentDockRequired = true;
  if (svc.appointment_delivery) delete svc.appointment_delivery;
  merged.additionalServices = svc;
}

function migrateLegacyCommercialFields(merged, raw) {
  const pptRaw = merged.preferredPaymentTerms;
  let arr = [];
  if (Array.isArray(pptRaw)) {
    arr = pptRaw.map((s) => String(s).trim()).filter(Boolean);
  } else if (pptRaw && typeof pptRaw === 'object') {
    arr = legacyPreferredPaymentTermsObjectToLabels(pptRaw);
  }
  if (String(merged.cashOnDelivery || '').toLowerCase() === 'yes') {
    arr.push('COD');
  }
  const pm = String(merged.paymentMode || '').toLowerCase();
  if (pm.includes('letter')) arr.push('Letter of Credit');
  if (pm.includes('escrow')) arr.push('Escrow');
  const ct = String(merged.creditTerms || '').trim();
  if (ct === 'Net 30') arr.push('Net 30');
  if (ct === 'Net 60') arr.push('Net 60');
  if (ct === 'COD') arr.push('COD');
  if (ct === 'Advance') arr.push('Advance Payment');
  merged.preferredPaymentTerms = [...new Set(arr.map((s) => String(s).trim()).filter(Boolean))];

  if (!merged.shipmentFrequency && raw && raw.shippingFrequency) {
    merged.shipmentFrequency = raw.shippingFrequency;
  }
  const ec = String(merged.expectedBudgetCurrency || '').toUpperCase();
  merged.expectedBudgetCurrency = /^[A-Z]{2,4}$/.test(ec) ? ec : 'USD';
  if (merged.quoteDeadline == null) merged.quoteDeadline = '';
  if (merged.expectedBudgetAmount == null) merged.expectedBudgetAmount = '';
  [
    'cashOnDelivery',
    'paymentMode',
    'accountManager',
    'salesPerson',
    'source',
    'leadSource',
    'shipToParty',
    'bankDetail',
    'quotationBasis',
  ].forEach((k) => {
    if (Object.prototype.hasOwnProperty.call(merged, k)) delete merged[k];
  });
}

function migrateLegacyCarrierFields(merged) {
  const pc = merged.preferredCarriers != null ? String(merged.preferredCarriers).trim() : '';
  const legacyPref = merged.preferredCarrier != null ? String(merged.preferredCarrier).trim() : '';
  merged.preferredCarriers = pc || legacyPref;
  const cr = merged.carrierRequirements != null ? String(merged.carrierRequirements).trim() : '';
  const legacyCp = merged.carrierPreferences != null ? String(merged.carrierPreferences).trim() : '';
  merged.carrierRequirements = cr || legacyCp;
  if (merged.excludedCarriers == null) merged.excludedCarriers = '';
  merged.docCommercialInvoice = Boolean(merged.docCommercialInvoice);
  merged.docPackingList = Boolean(merged.docPackingList);
  merged.docMsds = Boolean(merged.docMsds);
  ['preferredCarrier', 'carrierPreferences', 'carrierRating'].forEach((k) => {
    if (Object.prototype.hasOwnProperty.call(merged, k)) delete merged[k];
  });
}

function normalizeTimeHHMM(t) {
  if (t == null || String(t).trim() === '') return '';
  const s = String(t).trim();
  const m = s.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return '';
  const h = Math.min(23, Math.max(0, parseInt(m[1], 10)));
  const min = Math.min(59, Math.max(0, parseInt(m[2], 10)));
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

/** Drafts saved with free-text `pickupTimeWindow` / `deliveryTimeWindow` → from/to time fields. */
function migrateLegacyTimeWindows(merged) {
  if (merged.pickupTimeFrom == null) merged.pickupTimeFrom = '';
  if (merged.pickupTimeTo == null) merged.pickupTimeTo = '';
  if (merged.deliveryTimeFrom == null) merged.deliveryTimeFrom = '';
  if (merged.deliveryTimeTo == null) merged.deliveryTimeTo = '';

  const legacyPickup = merged.pickupTimeWindow;
  if (
    legacyPickup != null &&
    String(legacyPickup).trim() !== '' &&
    (!merged.pickupTimeFrom || !merged.pickupTimeTo)
  ) {
    const parts = String(legacyPickup).split(/\s*-\s*/);
    if (parts.length >= 2) {
      const a = normalizeTimeHHMM(parts[0].trim());
      const b = normalizeTimeHHMM(parts[1].trim());
      if (a) merged.pickupTimeFrom = merged.pickupTimeFrom || a;
      if (b) merged.pickupTimeTo = merged.pickupTimeTo || b;
    }
  }
  const legacyDel = merged.deliveryTimeWindow;
  if (
    legacyDel != null &&
    String(legacyDel).trim() !== '' &&
    (!merged.deliveryTimeFrom || !merged.deliveryTimeTo)
  ) {
    const parts = String(legacyDel).split(/\s*-\s*/);
    if (parts.length >= 2) {
      const a = normalizeTimeHHMM(parts[0].trim());
      const b = normalizeTimeHHMM(parts[1].trim());
      if (a) merged.deliveryTimeFrom = merged.deliveryTimeFrom || a;
      if (b) merged.deliveryTimeTo = merged.deliveryTimeTo || b;
    }
  }
  delete merged.pickupTimeWindow;
  delete merged.deliveryTimeWindow;
}

function migrateLegacySpecialInstructions(merged) {
  const sic =
    merged.specialInstructionsComments != null ? String(merged.specialInstructionsComments).trim() : '';
  const gbt = merged.generalBusinessTerms != null ? String(merged.generalBusinessTerms).trim() : '';
  const priv = merged.privacyDataPolicy != null ? String(merged.privacyDataPolicy).trim() : '';
  const notif = merged.notificationPreference != null ? String(merged.notificationPreference).trim() : '';
  if (!sic) {
    const parts = [gbt, priv && `Privacy & data: ${priv}`, notif && `Notification preference: ${notif}`].filter(Boolean);
    merged.specialInstructionsComments = parts.join('\n\n');
  } else {
    merged.specialInstructionsComments = sic;
  }
  if (merged.complianceRequirements == null) merged.complianceRequirements = '';
  if (merged.sustainabilityPreferences == null) merged.sustainabilityPreferences = '';
  merged.returnLogisticsRequired = Boolean(merged.returnLogisticsRequired);
  merged.termsAccepted = Boolean(merged.termsAccepted);
  ['generalBusinessTerms', 'privacyDataPolicy', 'notificationPreference'].forEach((k) => {
    if (Object.prototype.hasOwnProperty.call(merged, k)) delete merged[k];
  });
}

function mergeDraftIntoForm(companyId, raw) {
  if (!raw || typeof raw !== 'object') return getInitialGenericRfqForm(companyId);
  const base = getInitialGenericRfqForm(companyId);
  const merged = { ...base, ...raw, companyId: base.companyId };
  merged.packages =
    Array.isArray(raw.packages) && raw.packages.length > 0
      ? raw.packages.map((p, i) => ({
          ...emptyPackageRow(),
          ...p,
          weightKg: p.weightKg != null && p.weightKg !== '' ? p.weightKg : '',
          id: p.id || `pkg-${i}`,
        }))
      : [emptyPackageRow()];
  merged.documents =
    Array.isArray(raw.documents) && raw.documents.length > 0
      ? raw.documents.map((d, i) => ({
          ...emptyDocumentRow(),
          ...d,
          file: null,
          id: d.id || `doc-${i}`,
        }))
      : [emptyDocumentRow()];
  merged.additionalServices =
    raw.additionalServices && typeof raw.additionalServices === 'object'
      ? { ...raw.additionalServices }
      : {};
  migrateLegacyServiceFields(merged, raw);
  const ref = merged.rfqReferenceNumber != null ? String(merged.rfqReferenceNumber).trim() : '';
  merged.rfqReferenceNumber = ref || generateRfqReferenceNumber();
  const rd = merged.rfqDate != null ? String(merged.rfqDate).trim() : '';
  merged.rfqDate = rd || getTodayRfqDateString();
  const wu = String(merged.totalWeightUnit || '').toUpperCase();
  merged.totalWeightUnit = CARGO_WEIGHT_UNIT_OPTIONS.includes(wu) ? wu : 'KG';
  const vu = String(merged.totalVolumeUnit || '');
  merged.totalVolumeUnit = CARGO_VOLUME_UNIT_OPTIONS.includes(vu) ? vu : 'CBM';
  if (merged.urgencyLevel === 'Critical') merged.urgencyLevel = 'Urgent';
  if (typeof merged.flexibleDates !== 'boolean') {
    merged.flexibleDates = Boolean(merged.flexibleDates);
  }
  migrateLegacyCommercialFields(merged, raw);
  migrateLegacyCarrierFields(merged);
  migrateLegacySpecialInstructions(merged);
  migrateLegacyTimeWindows(merged);
  return merged;
}

function Section({ iconClass, icon, title, children }) {
  return (
    <section className="lgrq-section">
      <div className="lgrq-section-head">
        <span className={`lgrq-section-icon ${iconClass}`} aria-hidden>
          {icon}
        </span>
        <h2 className="lgrq-section-title">{title}</h2>
      </div>
      <div className="lgrq-section-body">{children}</div>
    </section>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <div className="lgrq-field">
      <label className="lgrq-label">
        {label}
        {required ? <span className="lgrq-required">*</span> : null}
      </label>
      {children}
      {hint ? <p className="lgrq-field-hint">{hint}</p> : null}
    </div>
  );
}

export default function LogisticsGenericRfqPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const p2pLaunchContext = useMemo(
    () => getEffectiveP2pLaunchContext(searchParams),
    [searchParams],
  );
  const companyIdParam = useMemo(() => {
    const raw = searchParams.get('companyId');
    if (raw == null || String(raw).trim() === '') return null;
    const match = String(raw).trim().match(/^(\d+)/);
    const n = match ? parseInt(match[1], 10) : NaN;
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [searchParams]);

  const salesOrderIdParam = useMemo(() => {
    const raw = searchParams.get('salesOrderId');
    const n = raw != null ? Number(raw) : NaN;
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [searchParams]);

  // New initiation source: ?sellerQuoteId=<quotation_id> coming from the
  // seller-rfq-list / seller_quotation_accept "Start Logistics RFQ" button on
  // an accepted quotation.
  const sellerQuoteIdParam = useMemo(() => {
    const raw = searchParams.get('sellerQuoteId');
    const n = raw != null ? Number(raw) : NaN;
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [searchParams]);

  const auctionIdParam = useMemo(() => {
    const raw = searchParams.get('auctionId');
    const n = raw != null ? Number(raw) : NaN;
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [searchParams]);

  const auctionSourceTypeParam = useMemo(() => {
    const raw = (searchParams.get('sourceType') || '').trim().toLowerCase();
    return raw === 'winning_bid' ? 'winning_bid' : 'auction_award';
  }, [searchParams]);

  const cmRfqIdParam = useMemo(() => {
    const raw = searchParams.get('cmRfqId');
    const n = raw != null ? Number(raw) : NaN;
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [searchParams]);

  // Used together with cmRfqIdParam; we don't want to mistakenly grab auction's
  // sourceType param if a CM URL is missing this field.
  const cmSourceTypeParam = useMemo(() => {
    if (cmRfqIdParam == null) return null;
    const raw = (searchParams.get('sourceType') || '').trim().toLowerCase();
    const allowed = new Set([
      'manufacturing_rfq',
      'manufacturing_quote',
      'manufacturing_sample_dispatch',
      'manufacturing_final_dispatch',
      'manufacturing_recurring_dispatch',
    ]);
    return allowed.has(raw) ? raw : 'manufacturing_rfq';
  }, [searchParams, cmRfqIdParam]);

  const cmLinkPurposeParam = useMemo(() => {
    if (cmRfqIdParam == null) return null;
    const raw = (searchParams.get('linkPurpose') || '').trim().toLowerCase();
    const allowed = new Set([
      'fulfillment',
      'raw_material_inward',
      'sample_dispatch',
      'final_dispatch',
      'recurring_batch',
    ]);
    return allowed.has(raw) ? raw : 'fulfillment';
  }, [searchParams, cmRfqIdParam]);

  const cmSourceRecordIdParam = useMemo(() => {
    if (cmRfqIdParam == null) return null;
    const raw = (searchParams.get('sourceRecordId') || '').trim();
    if (raw) return raw;
    // Derive default if caller omitted it.
    switch (cmSourceTypeParam) {
      case 'manufacturing_sample_dispatch':
        return `${cmRfqIdParam}:sample`;
      case 'manufacturing_final_dispatch':
        return `${cmRfqIdParam}:final`;
      case 'manufacturing_recurring_dispatch':
        return `${cmRfqIdParam}:recurring`;
      default:
        return String(cmRfqIdParam);
    }
  }, [searchParams, cmRfqIdParam, cmSourceTypeParam]);

  const [form, setForm] = useState(() => getInitialGenericRfqForm(companyIdParam));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const submitRfqInFlightRef = useRef(false);

  const originLoc = useLocationPicker();
  const destLoc = useLocationPicker();
  const { currencyOptions } = useCurrencyMaster();
  const { paymentTermSelectOptions: paymentTermMasterOptions, loading: paymentTermsLoading } =
    usePaymentTermsMaster();

  const cargoCurrencySelectOptions = useMemo(() => {
    if (Array.isArray(currencyOptions) && currencyOptions.length > 0) {
      const seen = new Set();
      return currencyOptions
        .map((opt) => {
          const raw =
            (opt.currencyCode != null && String(opt.currencyCode).trim()) ||
            (typeof opt.value === 'string' && String(opt.value).trim()) ||
            '';
          const code = /^[A-Za-z]{2,4}$/.test(raw) ? raw.toUpperCase() : raw;
          return { value: code, label: opt.label || code };
        })
        .filter((o) => o.value && !seen.has(o.value) && seen.add(o.value));
    }
    return VALUE_CURRENCY_OPTIONS.map((c) => ({ value: c, label: c }));
  }, [currencyOptions]);

  const paymentTermSelectOptions = useMemo(() => {
    const base = Array.isArray(paymentTermMasterOptions) ? paymentTermMasterOptions : [];
    const saved = Array.isArray(form.preferredPaymentTerms) ? form.preferredPaymentTerms : [];
    const extras = saved
      .filter((s) => s && !base.some((opt) => opt.value === String(s).trim()))
      .map((s) => {
        const v = String(s).trim();
        return { value: v, label: v };
      });
    return [...base, ...extras];
  }, [paymentTermMasterOptions, form.preferredPaymentTerms]);

  const paymentTermsSelectValue = useMemo(() => {
    const selected = Array.isArray(form.preferredPaymentTerms) ? form.preferredPaymentTerms : [];
    return paymentTermSelectOptions.filter((opt) => selected.includes(opt.value));
  }, [paymentTermSelectOptions, form.preferredPaymentTerms]);

  useEffect(() => {
    if (form.originCountry) {
      void originLoc.loadStates(String(form.originCountry));
    } else {
      void originLoc.loadStates('');
    }
  }, [form.originCountry, originLoc.loadStates]);

  useEffect(() => {
    if (form.originState) {
      void originLoc.loadCities(String(form.originState));
    } else {
      void originLoc.loadCities('');
    }
  }, [form.originState, originLoc.loadCities]);

  useEffect(() => {
    if (form.destCountry) {
      void destLoc.loadStates(String(form.destCountry));
    } else {
      void destLoc.loadStates('');
    }
  }, [form.destCountry, destLoc.loadStates]);

  useEffect(() => {
    if (form.destState) {
      void destLoc.loadCities(String(form.destState));
    } else {
      void destLoc.loadCities('');
    }
  }, [form.destState, destLoc.loadCities]);

  useEffect(() => {
    setForm((prev) => ({ ...prev, companyId: companyIdParam }));
  }, [companyIdParam]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(draftStorageKey(companyIdParam));
      if (!raw) return;
      const parsed = JSON.parse(raw);
      setForm(mergeDraftIntoForm(companyIdParam, parsed));
      toast.info('Restored saved draft.');
    } catch {
      /* ignore */
    }
  }, [companyIdParam]);

  /** Merge buyer / user_login fields into empty company section fields (draft-safe: only fills blanks). */
  const applyBuyerPrefill = useCallback(() => {
    return (async () => {
      const mapped = await fetchBuyerRefCompanyFieldsForRfqPrefill();
      setForm((prev) => mergeBuyerPrefillIntoForm(prev, mapped, 'generic'));
    })();
  }, []);

  useEffect(() => {
    void applyBuyerPrefill();
  }, [companyIdParam, applyBuyerPrefill]);

  // Seed moduleSource synchronously when arriving via ?sellerQuoteId so the
  // backend auto-link (logistics_generic_rfq -> seller_quote) fires reliably,
  // even if the prefill endpoint fails or is slow.
  useEffect(() => {
    if (sellerQuoteIdParam == null) return;
    setForm((prev) => ({
      ...prev,
      moduleSource: prev.moduleSource ?? {
        source_module: 'buyer_seller',
        source_record_type: 'seller_quote',
        source_record_id: String(sellerQuoteIdParam),
        link_purpose: 'fulfillment',
      },
    }));
  }, [sellerQuoteIdParam]);

  useEffect(() => {
    if (sellerQuoteIdParam == null) return;
    let cancelled = false;
    (async () => {
      try {
        const qs = new URLSearchParams({ seller_quote_id: String(sellerQuoteIdParam) });
        const resp = await CommonService.get(`/module-logistics/buyer-seller/prefill?${qs.toString()}`);
        const data = resp?.data ?? resp;
        const prefill = data?.prefill && typeof data.prefill === 'object' ? data.prefill : null;
        if (!prefill || cancelled) return;
        setForm((prev) => ({
          ...prev,
          ...prefill,
          moduleSource: prefill.moduleSource ?? prev.moduleSource,
          logisticsOwner: prefill.logisticsOwner ?? prev.logisticsOwner,
          rfqReferenceNumber: prefill.rfqReferenceNumber || prev.rfqReferenceNumber,
          productDescription: prefill.productDescription || prev.productDescription,
          shippingAddress: prefill.shippingAddress || prev.shippingAddress,
          companyName: prefill.companyName || prev.companyName,
          email: prefill.email || prev.email,
          contactPerson: prefill.contactPerson || prev.contactPerson,
          contactNumber: prefill.contactNumber || prev.contactNumber,
        }));
      } catch {
        /* prefill optional */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sellerQuoteIdParam]);

  useEffect(() => {
    if (salesOrderIdParam == null) return;
    let cancelled = false;
    (async () => {
      try {
        const qs = new URLSearchParams({ sales_order_id: String(salesOrderIdParam) });
        const resp = await CommonService.get(`/module-logistics/buyer-seller/prefill?${qs.toString()}`);
        const data = resp?.data ?? resp;
        const prefill = data?.prefill && typeof data.prefill === 'object' ? data.prefill : null;
        if (!prefill || cancelled) return;
        setForm((prev) => ({
          ...prev,
          ...prefill,
          moduleSource: prefill.moduleSource ?? prev.moduleSource,
          logisticsOwner: prefill.logisticsOwner ?? prev.logisticsOwner,
          rfqReferenceNumber: prefill.rfqReferenceNumber || prev.rfqReferenceNumber,
          productDescription: prefill.productDescription || prev.productDescription,
          shippingAddress: prefill.shippingAddress || prev.shippingAddress,
          companyName: prefill.companyName || prev.companyName,
          email: prefill.email || prev.email,
          contactPerson: prefill.contactPerson || prev.contactPerson,
          contactNumber: prefill.contactNumber || prev.contactNumber,
        }));
      } catch {
        /* prefill optional */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [salesOrderIdParam]);

  useEffect(() => {
    if (auctionIdParam == null) return;
    // Always seed moduleSource from the URL up-front so the auction → link
    // wiring survives even if the prefill endpoint 403s (e.g. user reloaded
    // the page, or seller hasn't fully accepted yet — the form still works).
    setForm((prev) => ({
      ...prev,
      moduleSource: prev.moduleSource ?? {
        source_module: 'auction',
        source_record_type: auctionSourceTypeParam,
        source_record_id: String(auctionIdParam),
        link_purpose: 'fulfillment',
        auction_id: auctionIdParam,
      },
    }));

    let cancelled = false;
    (async () => {
      try {
        const qs = new URLSearchParams({
          auction_id: String(auctionIdParam),
          source_record_type: auctionSourceTypeParam,
        });
        const resp = await CommonService.get(`/module-logistics/auction/prefill?${qs.toString()}`);
        const data = resp?.data ?? resp;
        const prefill = data?.prefill && typeof data.prefill === 'object' ? data.prefill : null;
        if (!prefill || cancelled) return;
        setForm((prev) => ({
          ...prev,
          ...prefill,
          moduleSource: prefill.moduleSource ?? prev.moduleSource,
          rfqReferenceNumber: prefill.rfqReferenceNumber || prev.rfqReferenceNumber,
          productDescription: prefill.productDescription || prev.productDescription,
          shippingAddress: prefill.shippingAddress || prev.shippingAddress,
          companyName: prefill.companyName || prev.companyName,
          email: prefill.email || prev.email,
          contactPerson: prefill.contactPerson || prev.contactPerson,
          contactNumber: prefill.contactNumber || prev.contactNumber,
        }));
      } catch {
        /* prefill optional — moduleSource was already seeded above */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auctionIdParam, auctionSourceTypeParam]);

  useEffect(() => {
    if (cmRfqIdParam == null) return;
    // Seed moduleSource synchronously from the URL so the CM → link wiring
    // survives even if the prefill endpoint fails. Mirrors the auction flow.
    setForm((prev) => ({
      ...prev,
      moduleSource: prev.moduleSource ?? {
        source_module: 'contract_manufacturing',
        source_record_type: cmSourceTypeParam,
        source_record_id: cmSourceRecordIdParam,
        link_purpose: cmLinkPurposeParam,
        cm_rfq_id: cmRfqIdParam,
      },
    }));

    let cancelled = false;
    (async () => {
      try {
        const qs = new URLSearchParams({
          cm_rfq_id: String(cmRfqIdParam),
          source_record_type: cmSourceTypeParam,
          link_purpose: cmLinkPurposeParam,
          source_record_id: cmSourceRecordIdParam,
        });
        const resp = await CommonService.get(`/module-logistics/contract-manufacturing/prefill?${qs.toString()}`);
        const data = resp?.data ?? resp;
        const prefill = data?.prefill && typeof data.prefill === 'object' ? data.prefill : null;
        if (!prefill || cancelled) return;
        setForm((prev) => ({
          ...prev,
          ...prefill,
          moduleSource: prefill.moduleSource ?? prev.moduleSource,
          rfqReferenceNumber: prefill.rfqReferenceNumber || prev.rfqReferenceNumber,
          productDescription: prefill.productDescription || prev.productDescription,
          shippingAddress: prefill.shippingAddress || prev.shippingAddress,
          companyName: prefill.companyName || prev.companyName,
          email: prefill.email || prev.email,
          contactPerson: prefill.contactPerson || prev.contactPerson,
          contactNumber: prefill.contactNumber || prev.contactNumber,
        }));
      } catch {
        /* prefill optional — moduleSource was already seeded above */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cmRfqIdParam, cmSourceTypeParam, cmLinkPurposeParam, cmSourceRecordIdParam]);

  const setVal = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const updatePackage = useCallback((index, key, value) => {
    setForm((prev) => {
      const packages = [...prev.packages];
      packages[index] = { ...packages[index], [key]: value };
      return { ...prev, packages };
    });
  }, []);

  const addPackage = useCallback(() => {
    setForm((prev) => ({ ...prev, packages: [...prev.packages, emptyPackageRow()] }));
  }, []);

  const removePackage = useCallback((index) => {
    setForm((prev) => {
      if (prev.packages.length <= 1) return prev;
      const packages = prev.packages.filter((_, i) => i !== index);
      return { ...prev, packages };
    });
  }, []);

  const updateDocument = useCallback((index, patch) => {
    setForm((prev) => {
      const documents = [...prev.documents];
      documents[index] = { ...documents[index], ...patch };
      return { ...prev, documents };
    });
  }, []);

  const addDocument = useCallback(() => {
    setForm((prev) => ({ ...prev, documents: [...prev.documents, emptyDocumentRow()] }));
  }, []);

  const removeDocument = useCallback((index) => {
    setForm((prev) => {
      if (prev.documents.length <= 1) return prev;
      return { ...prev, documents: prev.documents.filter((_, i) => i !== index) };
    });
  }, []);

  const onFileChange = useCallback((index, e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const sizeLabel =
      file.size >= 1024 * 1024
        ? `${(file.size / (1024 * 1024)).toFixed(2)} MB`
        : `${Math.max(1, Math.round(file.size / 1024))} KB`;
    updateDocument(index, { file, fileName: file.name, sizeLabel });
    e.target.value = '';
  }, [updateDocument]);

  const toggleAdditionalService = useCallback((key) => {
    setForm((prev) => {
      const next = { ...prev.additionalServices };
      if (next[key]) delete next[key];
      else next[key] = true;
      return { ...prev, additionalServices: next };
    });
  }, []);

  const saveDraft = useCallback(() => {
    setIsSavingDraft(true);
    try {
      localStorage.setItem(draftStorageKey(companyIdParam), JSON.stringify(serializeFormForDraft(form)));
      setForm((prev) => ({ ...prev, rfqStatus: 'draft' }));
      toast.success('Draft saved on this device. You can continue later from the same browser.');
    } catch {
      toast.error('Could not save draft.');
    } finally {
      setIsSavingDraft(false);
    }
  }, [form, companyIdParam]);

  const submitRfq = useCallback(async () => {
    if (!form.rfqDate?.trim()) {
      toast.error('Please select RFQ date.');
      return;
    }
    if (!form.companyName?.trim()) {
      toast.error('Please enter company name.');
      return;
    }
    if (!form.industry?.trim()) {
      toast.error('Please select industry.');
      return;
    }
    if (!form.contactPerson?.trim()) {
      toast.error('Please enter contact person.');
      return;
    }
    if (!form.contactNumber?.trim()) {
      toast.error('Please enter phone number.');
      return;
    }
    if (!form.email?.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      toast.error('Please enter a valid email address.');
      return;
    }
    if (!form.pickupDate?.trim()) {
      toast.error('Please select pickup date.');
      return;
    }
    if (!form.quoteDeadline?.trim()) {
      toast.error('Please select quote deadline.');
      return;
    }
    if (!form.termsAccepted) {
      toast.error('Please accept the terms and conditions to submit.');
      return;
    }
    if (submitRfqInFlightRef.current) return;
    submitRfqInFlightRef.current = true;
    const snapshot = {
      ...form,
      companyId: companyIdParam ?? form.companyId ?? null,
      rfqStatus: 'submitted',
      submittedAt: new Date().toISOString(),
      pickupTimeWindow: formatTimeRangeForPayload(form.pickupTimeFrom, form.pickupTimeTo),
      deliveryTimeWindow: formatTimeRangeForPayload(form.deliveryTimeFrom, form.deliveryTimeTo),
      documents: (form.documents || []).map((d) => ({
        fileName: d.fileName,
        sizeLabel: d.sizeLabel,
        hasFile: Boolean(d.file),
      })),
      ...(p2pLaunchContext?.p2p_transaction_id
        ? {
            p2pContext: {
              p2p_transaction_id: String(p2pLaunchContext.p2p_transaction_id),
              p2p_organization_id: p2pLaunchContext.p2p_organization_id
                ? Number(p2pLaunchContext.p2p_organization_id)
                : null,
              source_context: 'p2p',
              category_id: p2pLaunchContext.category_id
                ? Number(p2pLaunchContext.category_id)
                : null,
              return_to: p2pLaunchContext.return_to || 'p2p_transaction_detail',
            },
          }
        : {}),
    };
    setIsSubmitting(true);
    try {
      if (p2pLaunchContext?.p2p_transaction_id) {
        await submitP2pLogisticsRfq({
          payload: snapshot,
          rfqKind: 'generic',
          p2pLaunchContext,
          navigate,
          searchParams,
        });
        try {
          localStorage.removeItem(draftStorageKey(companyIdParam));
        } catch {
          /* ignore */
        }
        return;
      }

      const matchRes = await CommonService.post('/company/logistics-providers/rfq/match', {
        payload: snapshot,
        rfqKind: 'generic',
        forceIncludeCompanyId: companyIdParam,
      });

      if (!matchRes?.success) {
        toast.error(matchRes?.message || 'Could not match logistics providers.');
        return;
      }

      const matchedItems = matchRes?.data?.items || [];
      if (matchedItems.length === 0) {
        toast.info('No matching logistics providers found.');
        navigate('/logistics/my-rfqs', { replace: true });
        return;
      }

      const saved = saveLogisticsRfqPendingSubmit({
        payload: snapshot,
        rfqKind: 'generic',
        companyIdParam,
        matchResult: matchRes.data,
      });
      if (!saved) {
        toast.error('Could not save RFQ session. Please try again.');
        return;
      }

      try {
        localStorage.removeItem(draftStorageKey(companyIdParam));
      } catch {
        /* ignore */
      }

      const providersPath =
        companyIdParam != null
          ? `/logistics/rfq/generic/providers?companyId=${companyIdParam}`
          : '/logistics/rfq/generic/providers';
      navigate(providersPath);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Could not find matching providers. Please try again.';
      toast.error(typeof msg === 'string' ? msg : 'Could not find matching providers.');
    } finally {
      submitRfqInFlightRef.current = false;
      setIsSubmitting(false);
    }
  }, [form, companyIdParam, navigate, p2pLaunchContext, searchParams]);

  const providerLabel =
    companyIdParam != null ? `Sending to provider company ID: ${companyIdParam}` : 'No specific provider selected';

  const shipmentTypeOptions = useMemo(
    () => getShipmentTypesForTransportMode(form.transportMode),
    [form.transportMode]
  );

  const transportModeLabel = useMemo(() => {
    const m = LOGISTICS_TRANSPORT_MODES.find((x) => x.id === form.transportMode);
    return m?.label || 'Road';
  }, [form.transportMode]);

  const setTransportMode = useCallback((modeId) => {
    const mode = String(modeId || 'road').toLowerCase();
    setForm((prev) => {
      const opts = getShipmentTypesForTransportMode(mode);
      const keep = prev.modeDetail && opts.includes(prev.modeDetail) ? prev.modeDetail : '';
      return { ...prev, transportMode: mode, modeDetail: keep };
    });
  }, []);

  return (
    <div className="lgrq-page">
      <WebsiteTopBar />
      <main className="lgrq-main">
        <header className="lgrq-page-hero">
          <Link to="/home" className="lgrq-back lgrq-back--hero">
            ← Back
          </Link>
          <div className="lgrq-page-hero-text">
            <h1 className="lgrq-page-title">Request for Quotation (RFQ)</h1>
            <p className="lgrq-page-subtitle">
              Complete all required fields to get competitive quotes from verified providers
            </p>
          </div>
        </header>
        <div className="lgrq-provider-pill">{providerLabel}</div>

        <section className="lgrq-section lgrq-section--ref-company">
          <header className="lgrq-section-head lgrq-section-head--ref-company">
            <span className="lgrq-section-icon lgrq-section-icon--building" aria-hidden>
              🏢
            </span>
            <div className="lgrq-section-head-titles">
              <h2 className="lgrq-section-title lgrq-section-title--ref-company">RFQ Reference &amp; Company Information</h2>
              <p className="lgrq-section-subtitle">Basic details about your company and this RFQ</p>
            </div>
          </header>
          <div className="lgrq-section-body lgrq-section-body--ref-company">
            <div className="lgrq-row lgrq-row--4 lgrq-ref-company-grid">
              <Field label="RFQ Reference Number" hint="Auto-generated for this RFQ (read-only).">
                <input
                  className="lgrq-input lgrq-input--readonly"
                  value={form.rfqReferenceNumber}
                  readOnly
                  tabIndex={0}
                  aria-readonly="true"
                  title={form.rfqReferenceNumber}
                />
              </Field>
              <Field label="RFQ Date" required>
                <div className="lgrq-input-wrap lgrq-input-wrap--date">
                  <input
                    type="date"
                    className="lgrq-input lgrq-input--date"
                    value={form.rfqDate}
                    onChange={(e) => setVal('rfqDate', e.target.value)}
                  />
                  <span className="lgrq-input-suffix-icon" aria-hidden>
                    <FaCalendarAlt />
                  </span>
                </div>
              </Field>
              <Field label="Company Name" required>
                <input
                  className="lgrq-input"
                  value={form.companyName}
                  onChange={(e) => setVal('companyName', e.target.value)}
                  placeholder="e.g., ABC Manufacturing Pvt. Ltd."
                />
              </Field>
              <Field label="Industry" required>
                <div className="lgrq-select-wrap">
                  <select className="lgrq-select" value={form.industry} onChange={(e) => setVal('industry', e.target.value)}>
                    <option value="">Select industry</option>
                    {LOGISTICS_INDUSTRIES_SERVED.map((ind) => (
                      <option key={ind} value={ind}>
                        {ind}
                      </option>
                    ))}
                  </select>
                </div>
              </Field>
            </div>
            <div className="lgrq-row lgrq-row--4 lgrq-ref-company-grid lgrq-ref-company-grid--gap-top">
              <Field label="Contact Person" required>
                <input
                  className="lgrq-input"
                  value={form.contactPerson}
                  onChange={(e) => setVal('contactPerson', e.target.value)}
                  placeholder="e.g., Amit Sharma"
                />
              </Field>
              <Field label="Email Address" required>
                <input
                  type="email"
                  className="lgrq-input"
                  value={form.email}
                  onChange={(e) => setVal('email', e.target.value)}
                  placeholder="amit@company.com"
                />
              </Field>
              <Field label="Phone Number" required>
                <input
                  className="lgrq-input"
                  value={form.contactNumber}
                  onChange={(e) => setVal('contactNumber', e.target.value)}
                  placeholder="+91 98765 43210"
                />
              </Field>
              <Field label="Company Address">
                <input
                  className="lgrq-input"
                  value={form.companyAddress}
                  onChange={(e) => setVal('companyAddress', e.target.value)}
                  placeholder="Registered office address"
                />
              </Field>
            </div>
          </div>
        </section>

        <Section iconClass="lgrq-section-icon--yellow" icon="🚚" title="Transport mode & shipment type">
          <div className="lgrq-mode-tabs" role="tablist">
            {LOGISTICS_TRANSPORT_MODES.map((m) => (
              <button
                key={m.id}
                type="button"
                role="tab"
                aria-selected={form.transportMode === m.id}
                className={`lgrq-mode-tab${form.transportMode === m.id ? ' active' : ''}`}
                onClick={() => setTransportMode(m.id)}
              >
                <span className="lgrq-mode-tab-icon">{m.icon}</span>
                {m.label}
              </button>
            ))}
          </div>
          <div className="lgrq-row lgrq-row--4 lgrq-transport-shipment-row">
            <Field
              label="Shipment type"
              required
              hint={`Options for ${transportModeLabel} — change transport mode above to refresh this list.`}
            >
              <select
                key={form.transportMode}
                className="lgrq-select"
                aria-label={`Shipment type for ${transportModeLabel}`}
                value={shipmentTypeOptions.includes(form.modeDetail) ? form.modeDetail : ''}
                onChange={(e) => setVal('modeDetail', e.target.value)}
              >
                <option value="">Select type</option>
                {shipmentTypeOptions.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Incoterms" required hint="Defines cost & risk responsibility">
              <select className="lgrq-select" value={form.incoterm} onChange={(e) => setVal('incoterm', e.target.value)}>
                <option value="">Select Incoterm</option>
                {INCOTERM_OPTIONS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Commodity type" required>
              <select className="lgrq-select" value={form.commodityType} onChange={(e) => setVal('commodityType', e.target.value)}>
                <option value="">Select commodity</option>
                {COMMODITY_TYPE_OPTIONS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="HS Code" hint="For customs classification">
              <input
                className="lgrq-input"
                value={form.hsCode}
                onChange={(e) => setVal('hsCode', e.target.value)}
                placeholder="e.g., 8471.30"
                autoComplete="off"
              />
            </Field>
          </div>
          <div className="lgrq-info-box">
            Shipment type options update when you change transport mode. Incoterms set who pays for freight, insurance,
            and customs handover.
          </div>
        </Section>

        <Section iconClass="lgrq-section-icon--green" icon="📍" title="Origin / pickup location">
          <div className="lgrq-loc-address-row">
            <Field label="Address">
              <input
                className="lgrq-input"
                value={form.originAddress}
                onChange={(e) => setVal('originAddress', e.target.value)}
                placeholder="Street, building, landmark"
              />
            </Field>
          </div>
          <div className="lgrq-row lgrq-row--4">
            <Field label="Country">
              <div className="lgrq-select-wrap">
                <select
                  className="lgrq-select"
                  value={form.originCountry != null && form.originCountry !== '' ? String(form.originCountry) : ''}
                  disabled={originLoc.loading}
                  onChange={(e) => {
                    const v = e.target.value;
                    setForm((prev) => ({ ...prev, originCountry: v, originState: '', originCity: '' }));
                    void originLoc.loadStates(v);
                  }}
                >
                  <option value="">{originLoc.loading ? 'Loading…' : 'Select country'}</option>
                  {originLoc.countries.map((c) => (
                    <option key={String(c.id)} value={String(c.id)}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
            </Field>
            <Field label="State">
              <div className="lgrq-select-wrap">
                <select
                  className="lgrq-select"
                  value={form.originState != null && form.originState !== '' ? String(form.originState) : ''}
                  disabled={!form.originCountry || originLoc.loadingStates}
                  onChange={(e) => {
                    const v = e.target.value;
                    setForm((prev) => ({ ...prev, originState: v, originCity: '' }));
                    void originLoc.loadCities(v);
                  }}
                >
                  <option value="">{originLoc.loadingStates ? 'Loading…' : 'Select state'}</option>
                  {originLoc.states.map((s) => (
                    <option key={String(s.id)} value={String(s.id)}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
            </Field>
            <Field label="City / town">
              <div className="lgrq-select-wrap">
                <select
                  className="lgrq-select"
                  value={form.originCity != null && form.originCity !== '' ? String(form.originCity) : ''}
                  disabled={!form.originState || originLoc.loadingCities}
                  onChange={(e) => setVal('originCity', e.target.value)}
                >
                  <option value="">{originLoc.loadingCities ? 'Loading…' : 'Select city'}</option>
                  {originLoc.cities.map((c) => (
                    <option key={String(c.id)} value={String(c.id)}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
            </Field>
            <Field label="Pincode">
              <input className="lgrq-input" value={form.originZip} onChange={(e) => setVal('originZip', e.target.value)} />
            </Field>
          </div>
          <div className="lgrq-row lgrq-row--2">
            <Field label="Contact person name">
              <input
                className="lgrq-input"
                value={form.originContactName}
                onChange={(e) => setVal('originContactName', e.target.value)}
              />
            </Field>
            <Field label="Contact number">
              <input
                className="lgrq-input"
                value={form.originContactNumber}
                onChange={(e) => setVal('originContactNumber', e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section iconClass="lgrq-section-icon--red" icon="🎯" title="Destination / delivery location">
          <div className="lgrq-loc-address-row">
            <Field label="Address">
              <input
                className="lgrq-input"
                value={form.destAddress}
                onChange={(e) => setVal('destAddress', e.target.value)}
                placeholder="Street, building, landmark"
              />
            </Field>
          </div>
          <div className="lgrq-row lgrq-row--4">
            <Field label="Country">
              <div className="lgrq-select-wrap">
                <select
                  className="lgrq-select"
                  value={form.destCountry != null && form.destCountry !== '' ? String(form.destCountry) : ''}
                  disabled={destLoc.loading}
                  onChange={(e) => {
                    const v = e.target.value;
                    setForm((prev) => ({ ...prev, destCountry: v, destState: '', destCity: '' }));
                    void destLoc.loadStates(v);
                  }}
                >
                  <option value="">{destLoc.loading ? 'Loading…' : 'Select country'}</option>
                  {destLoc.countries.map((c) => (
                    <option key={String(c.id)} value={String(c.id)}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
            </Field>
            <Field label="State">
              <div className="lgrq-select-wrap">
                <select
                  className="lgrq-select"
                  value={form.destState != null && form.destState !== '' ? String(form.destState) : ''}
                  disabled={!form.destCountry || destLoc.loadingStates}
                  onChange={(e) => {
                    const v = e.target.value;
                    setForm((prev) => ({ ...prev, destState: v, destCity: '' }));
                    void destLoc.loadCities(v);
                  }}
                >
                  <option value="">{destLoc.loadingStates ? 'Loading…' : 'Select state'}</option>
                  {destLoc.states.map((s) => (
                    <option key={String(s.id)} value={String(s.id)}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
            </Field>
            <Field label="City / town">
              <div className="lgrq-select-wrap">
                <select
                  className="lgrq-select"
                  value={form.destCity != null && form.destCity !== '' ? String(form.destCity) : ''}
                  disabled={!form.destState || destLoc.loadingCities}
                  onChange={(e) => setVal('destCity', e.target.value)}
                >
                  <option value="">{destLoc.loadingCities ? 'Loading…' : 'Select city'}</option>
                  {destLoc.cities.map((c) => (
                    <option key={String(c.id)} value={String(c.id)}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
            </Field>
            <Field label="Pincode">
              <input className="lgrq-input" value={form.destZip} onChange={(e) => setVal('destZip', e.target.value)} />
            </Field>
          </div>
          <div className="lgrq-row lgrq-row--2">
            <Field label="Contact person name">
              <input
                className="lgrq-input"
                value={form.destContactName}
                onChange={(e) => setVal('destContactName', e.target.value)}
              />
            </Field>
            <Field label="Contact number">
              <input
                className="lgrq-input"
                value={form.destContactNumber}
                onChange={(e) => setVal('destContactNumber', e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <section className="lgrq-section lgrq-section--cargo">
          <header className="lgrq-section-head lgrq-section-head--cargo">
            <span className="lgrq-section-icon lgrq-section-icon--cargo-cube" aria-hidden>
              📦
            </span>
            <div className="lgrq-section-head-titles">
              <h2 className="lgrq-section-title lgrq-section-title--cargo">Cargo Details &amp; Specifications</h2>
              <p className="lgrq-cargo-subtitle">Detailed information about your cargo</p>
            </div>
          </header>
          <div className="lgrq-section-body lgrq-section-body--cargo">
            <Field label="Cargo Description" required>
              <textarea
                className="lgrq-textarea lgrq-textarea--cargo"
                value={form.cargoDescription}
                onChange={(e) => setVal('cargoDescription', e.target.value)}
                rows={5}
                placeholder="Describe the goods being shipped (e.g., Electronic components, textile machinery, pharmaceuticals...)"
              />
            </Field>
            <div className="lgrq-row lgrq-row--4 lgrq-cargo-metrics">
              <Field label="Number of Packages" required>
                <input
                  type="number"
                  min={0}
                  className="lgrq-input"
                  value={form.totalUnits}
                  onChange={(e) => setVal('totalUnits', e.target.value)}
                  placeholder="25"
                />
              </Field>
              <Field label="Total Weight" required>
                <div className="lgrq-inline-value-unit">
                  <input
                    type="number"
                    min={0}
                    className="lgrq-input lgrq-input--grow"
                    value={form.totalWeightValue}
                    onChange={(e) => setVal('totalWeightValue', e.target.value)}
                    placeholder="5000"
                  />
                  <div className="lgrq-select-wrap lgrq-select-wrap--narrow">
                    <select
                      className="lgrq-select"
                      value={form.totalWeightUnit}
                      onChange={(e) => setVal('totalWeightUnit', e.target.value)}
                    >
                      {CARGO_WEIGHT_UNIT_OPTIONS.map((u) => (
                        <option key={u} value={u}>
                          {u}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </Field>
              <Field label="Total Volume" required>
                <div className="lgrq-inline-value-unit">
                  <input
                    type="number"
                    min={0}
                    step="any"
                    className="lgrq-input lgrq-input--grow"
                    value={form.totalVolumeValue}
                    onChange={(e) => setVal('totalVolumeValue', e.target.value)}
                    placeholder="10"
                  />
                  <div className="lgrq-select-wrap lgrq-select-wrap--narrow">
                    <select
                      className="lgrq-select"
                      value={form.totalVolumeUnit}
                      onChange={(e) => setVal('totalVolumeUnit', e.target.value)}
                    >
                      {CARGO_VOLUME_UNIT_OPTIONS.map((u) => (
                        <option key={u} value={u}>
                          {u}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </Field>
              <Field label="Cargo Value" required>
                <div className="lgrq-inline-value-unit">
                  <input
                    type="number"
                    min={0}
                    className="lgrq-input lgrq-input--grow"
                    value={form.cargoValue}
                    onChange={(e) => setVal('cargoValue', e.target.value)}
                    placeholder="50000"
                  />
                  <div className="lgrq-select-wrap lgrq-select-wrap--narrow">
                    <select
                      className="lgrq-select"
                      value={form.cargoValueCurrency}
                      onChange={(e) => setVal('cargoValueCurrency', e.target.value)}
                    >
                      {cargoCurrencySelectOptions.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </Field>
            </div>

            <div className="lgrq-package-dim-head">
              <h3 className="lgrq-package-dim-title">Package Dimensions (Optional but Recommended)</h3>
              <button type="button" className="lgrq-btn-add-outline" onClick={addPackage}>
                <span className="lgrq-btn-add-outline-plus" aria-hidden>
                  +
                </span>
                Add Package Type
              </button>
            </div>
            <div className="lgrq-package-cards">
              {form.packages.map((row, i) => (
                <div key={row.id} className="lgrq-package-card">
                  <div className="lgrq-package-card-head">
                    <span className="lgrq-package-card-label">Package #{i + 1}</span>
                    {form.packages.length > 1 ? (
                      <button type="button" className="lgrq-btn-remove" onClick={() => removePackage(i)}>
                        Remove
                      </button>
                    ) : null}
                  </div>
                  <div className="lgrq-package-dim-fields">
                    <div className="lgrq-package-dim-field">
                      <span className="lgrq-package-dim-field-label">Length (cm)</span>
                      <input
                        type="number"
                        min={0}
                        step="any"
                        className="lgrq-input"
                        value={row.length === '' || row.length == null ? '' : row.length}
                        onChange={(e) => updatePackage(i, 'length', e.target.value)}
                        placeholder="120"
                      />
                    </div>
                    <div className="lgrq-package-dim-field">
                      <span className="lgrq-package-dim-field-label">Width (cm)</span>
                      <input
                        type="number"
                        min={0}
                        step="any"
                        className="lgrq-input"
                        value={row.width === '' || row.width == null ? '' : row.width}
                        onChange={(e) => updatePackage(i, 'width', e.target.value)}
                        placeholder="80"
                      />
                    </div>
                    <div className="lgrq-package-dim-field">
                      <span className="lgrq-package-dim-field-label">Height (cm)</span>
                      <input
                        type="number"
                        min={0}
                        step="any"
                        className="lgrq-input"
                        value={row.height === '' || row.height == null ? '' : row.height}
                        onChange={(e) => updatePackage(i, 'height', e.target.value)}
                        placeholder="100"
                      />
                    </div>
                    <div className="lgrq-package-dim-field">
                      <span className="lgrq-package-dim-field-label">Weight (kg)</span>
                      <input
                        type="number"
                        min={0}
                        step="any"
                        className="lgrq-input"
                        value={row.weightKg === '' || row.weightKg == null ? '' : row.weightKg}
                        onChange={(e) => updatePackage(i, 'weightKg', e.target.value)}
                        placeholder="200"
                      />
                    </div>
                    <div className="lgrq-package-dim-field">
                      <span className="lgrq-package-dim-field-label">Quantity</span>
                      <input
                        type="number"
                        min={0}
                        step={1}
                        className="lgrq-input"
                        value={row.quantity === '' || row.quantity == null ? '' : row.quantity}
                        onChange={(e) => updatePackage(i, 'quantity', e.target.value)}
                        placeholder="10"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <h3 className="lgrq-cargo-block-title">Cargo Characteristics</h3>
            <div className="lgrq-cargo-char-grid">
              {[
                ['charGeneralCargo', 'General Cargo'],
                ['charOversized', 'Oversized'],
                ['charPerishable', 'Perishable'],
                ['charHeavyMachinery', 'Heavy Machinery'],
                ['charFragile', 'Fragile'],
                ['charValuableGoods', 'Valuable Goods'],
                ['charHazardous', 'Hazardous'],
                ['charDocuments', 'Documents'],
              ].map(([key, label]) => (
                <label key={key} className="lgrq-check lgrq-check--cargo-char">
                  <input
                    type="checkbox"
                    checked={Boolean(form[key])}
                    onChange={(e) => setVal(key, e.target.checked)}
                  />
                  {label}
                </label>
              ))}
            </div>

            <h3 className="lgrq-cargo-block-title lgrq-cargo-block-title--spaced">Special Requirements</h3>
            <div className="lgrq-special-req-grid">
              <div className="lgrq-special-req-card lgrq-special-req-card--temp">
                <span className="lgrq-special-req-card__icon" aria-hidden>
                  🌡️
                </span>
                <div className="lgrq-special-req-card__body">
                  <p className="lgrq-special-req-card__title">Temperature Controlled</p>
                  <p className="lgrq-special-req-card__desc">Requires refrigeration</p>
                </div>
                <label className="lgrq-switch lgrq-switch--accent">
                  <input
                    type="checkbox"
                    checked={form.cargoTemperatureControlled}
                    onChange={(e) => setVal('cargoTemperatureControlled', e.target.checked)}
                  />
                  <span className="lgrq-switch-slider" />
                </label>
              </div>
              <div className="lgrq-special-req-card lgrq-special-req-card--haz">
                <span className="lgrq-special-req-card__icon" aria-hidden>
                  ⚠️
                </span>
                <div className="lgrq-special-req-card__body">
                  <p className="lgrq-special-req-card__title">Hazardous Material</p>
                  <p className="lgrq-special-req-card__desc">DG cargo</p>
                </div>
                <label className="lgrq-switch lgrq-switch--accent">
                  <input
                    type="checkbox"
                    checked={form.specialHazardousMaterial}
                    onChange={(e) => setVal('specialHazardousMaterial', e.target.checked)}
                  />
                  <span className="lgrq-switch-slider" />
                </label>
              </div>
              <div className="lgrq-special-req-card lgrq-special-req-card--stack">
                <span className="lgrq-special-req-card__icon" aria-hidden>
                  📦
                </span>
                <div className="lgrq-special-req-card__body">
                  <p className="lgrq-special-req-card__title">Stackable</p>
                  <p className="lgrq-special-req-card__desc">Can be stacked</p>
                </div>
                <label className="lgrq-switch lgrq-switch--accent">
                  <input
                    type="checkbox"
                    checked={form.specialStackable}
                    onChange={(e) => setVal('specialStackable', e.target.checked)}
                  />
                  <span className="lgrq-switch-slider" />
                </label>
              </div>
              <div className="lgrq-special-req-card lgrq-special-req-card--frag">
                <span className="lgrq-special-req-card__icon" aria-hidden>
                  ❗
                </span>
                <div className="lgrq-special-req-card__body">
                  <p className="lgrq-special-req-card__title">Fragile</p>
                  <p className="lgrq-special-req-card__desc">Handle with care</p>
                </div>
                <label className="lgrq-switch lgrq-switch--accent">
                  <input
                    type="checkbox"
                    checked={form.fragileHandling}
                    onChange={(e) => setVal('fragileHandling', e.target.checked)}
                  />
                  <span className="lgrq-switch-slider" />
                </label>
              </div>
            </div>
          </div>
        </section>

        <section className="lgrq-section lgrq-section--timeline">
          <header className="lgrq-section-head lgrq-section-head--timeline">
            <span className="lgrq-section-icon lgrq-section-icon--timeline-cal" aria-hidden>
              <FaRegCalendarAlt />
            </span>
            <div className="lgrq-section-head-titles">
              <h2 className="lgrq-section-title lgrq-section-title--timeline">Timeline &amp; Service Level</h2>
              <p className="lgrq-section-subtitle lgrq-section-subtitle--timeline">Pickup/delivery schedule and urgency</p>
            </div>
          </header>
          <div className="lgrq-section-body lgrq-section-body--timeline">
            <div className="lgrq-row lgrq-row--2">
              <Field label="Pickup Date" required>
                <div className="lgrq-input-wrap lgrq-input-wrap--date">
                  <input
                    type="date"
                    className="lgrq-input lgrq-input--date"
                    value={form.pickupDate}
                    onChange={(e) => setVal('pickupDate', e.target.value)}
                    placeholder="dd-mm-yyyy"
                    title="dd-mm-yyyy"
                  />
                  <span className="lgrq-input-suffix-icon" aria-hidden>
                    <FaCalendarAlt />
                  </span>
                </div>
              </Field>
              <Field label="Expected Delivery Date">
                <div className="lgrq-input-wrap lgrq-input-wrap--date">
                  <input
                    type="date"
                    className="lgrq-input lgrq-input--date"
                    value={form.deliveryDate}
                    onChange={(e) => setVal('deliveryDate', e.target.value)}
                    placeholder="dd-mm-yyyy"
                    title="dd-mm-yyyy"
                  />
                  <span className="lgrq-input-suffix-icon" aria-hidden>
                    <FaCalendarAlt />
                  </span>
                </div>
              </Field>
            </div>
            <div className="lgrq-row lgrq-row--2 lgrq-timeline-time-row">
              <Field label="Pickup time window" hint="Start and end time (optional).">
                <div className="lgrq-time-range">
                  <input
                    type="time"
                    className="lgrq-input lgrq-input--time"
                    value={form.pickupTimeFrom === '' || form.pickupTimeFrom == null ? '' : form.pickupTimeFrom}
                    onChange={(e) => setVal('pickupTimeFrom', e.target.value)}
                    aria-label="Pickup from"
                  />
                  <span className="lgrq-time-range-sep">to</span>
                  <input
                    type="time"
                    className="lgrq-input lgrq-input--time"
                    value={form.pickupTimeTo === '' || form.pickupTimeTo == null ? '' : form.pickupTimeTo}
                    onChange={(e) => setVal('pickupTimeTo', e.target.value)}
                    aria-label="Pickup to"
                  />
                </div>
              </Field>
              <Field label="Delivery time window" hint="Start and end time (optional).">
                <div className="lgrq-time-range">
                  <input
                    type="time"
                    className="lgrq-input lgrq-input--time"
                    value={form.deliveryTimeFrom === '' || form.deliveryTimeFrom == null ? '' : form.deliveryTimeFrom}
                    onChange={(e) => setVal('deliveryTimeFrom', e.target.value)}
                    aria-label="Delivery from"
                  />
                  <span className="lgrq-time-range-sep">to</span>
                  <input
                    type="time"
                    className="lgrq-input lgrq-input--time"
                    value={form.deliveryTimeTo === '' || form.deliveryTimeTo == null ? '' : form.deliveryTimeTo}
                    onChange={(e) => setVal('deliveryTimeTo', e.target.value)}
                    aria-label="Delivery to"
                  />
                </div>
              </Field>
            </div>

            <div className="lgrq-timeline-urgency">
              <p className="lgrq-timeline-urgency-label" id="lgrq-urgency-level-label">
                Urgency Level
              </p>
              <div className="lgrq-urgency-grid" role="radiogroup" aria-labelledby="lgrq-urgency-level-label">
                {TIMELINE_URGENCY_CARDS.map((card) => (
                  <label
                    key={card.value}
                    className={`lgrq-urgency-card${form.urgencyLevel === card.value ? ' lgrq-urgency-card--selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="lgrq-urgency-level"
                      className="lgrq-urgency-input"
                      value={card.value}
                      checked={form.urgencyLevel === card.value}
                      onChange={() => setVal('urgencyLevel', card.value)}
                    />
                    <span className="lgrq-urgency-card-body">
                      <span className="lgrq-urgency-card-title">{card.title}</span>
                      <span className="lgrq-urgency-card-desc">{card.desc}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="lgrq-flexible-dates">
              <label className="lgrq-flexible-dates-inner">
                <input
                  type="checkbox"
                  checked={form.flexibleDates}
                  onChange={(e) => setVal('flexibleDates', e.target.checked)}
                />
                <span>I have flexible dates (may help get better rates)</span>
              </label>
            </div>
          </div>
        </section>

        <section className="lgrq-section lgrq-section--service-req">
          <header className="lgrq-section-head lgrq-section-head--service-req">
            <span className="lgrq-section-icon lgrq-section-icon--service-shield" aria-hidden>
              <FaShieldAlt />
            </span>
            <div className="lgrq-section-head-titles">
              <h2 className="lgrq-section-title lgrq-section-title--service-req">Service Requirements &amp; Additional Services</h2>
              <p className="lgrq-section-subtitle lgrq-section-subtitle--service-req">
                Insurance, customs, and value-added services
              </p>
            </div>
          </header>
          <div className="lgrq-section-body lgrq-section-body--service-req">
            <div className="lgrq-svc-primary-grid">
              <div className="lgrq-svc-primary-card lgrq-svc-primary-card--insurance">
                <span className="lgrq-svc-primary-card__icon lgrq-svc-primary-card__icon--blue" aria-hidden>
                  <FaShieldAlt />
                </span>
                <div className="lgrq-svc-primary-card__body">
                  <p className="lgrq-svc-primary-card__title">Cargo Insurance Required</p>
                  <p className="lgrq-svc-primary-card__desc">Protect against loss/damage</p>
                </div>
                <label className="lgrq-switch lgrq-switch--accent">
                  <input
                    type="checkbox"
                    checked={form.cargoInsuranceRequired}
                    onChange={(e) => setVal('cargoInsuranceRequired', e.target.checked)}
                  />
                  <span className="lgrq-switch-slider" />
                </label>
              </div>
              <div className="lgrq-svc-primary-card lgrq-svc-primary-card--customs">
                <span className="lgrq-svc-primary-card__icon lgrq-svc-primary-card__icon--purple" aria-hidden>
                  <FaFileAlt />
                </span>
                <div className="lgrq-svc-primary-card__body">
                  <p className="lgrq-svc-primary-card__title">Customs Clearance Support</p>
                  <p className="lgrq-svc-primary-card__desc">Need documentation help</p>
                </div>
                <label className="lgrq-switch lgrq-switch--accent">
                  <input
                    type="checkbox"
                    checked={form.customsClearanceSupport}
                    onChange={(e) => setVal('customsClearanceSupport', e.target.checked)}
                  />
                  <span className="lgrq-switch-slider" />
                </label>
              </div>
              <div className="lgrq-svc-primary-card lgrq-svc-primary-card--broker">
                <span className="lgrq-svc-primary-card__icon lgrq-svc-primary-card__icon--green" aria-hidden>
                  <FaBriefcase />
                </span>
                <div className="lgrq-svc-primary-card__body">
                  <p className="lgrq-svc-primary-card__title">Customs Broker Needed</p>
                  <p className="lgrq-svc-primary-card__desc">Professional broker service</p>
                </div>
                <label className="lgrq-switch lgrq-switch--accent">
                  <input
                    type="checkbox"
                    checked={form.customsBrokerNeeded}
                    onChange={(e) => setVal('customsBrokerNeeded', e.target.checked)}
                  />
                  <span className="lgrq-switch-slider" />
                </label>
              </div>
            </div>

            <hr className="lgrq-service-divider" />

            <h3 className="lgrq-svc-block-title">Additional Services</h3>
            <div className="lgrq-additional-svc-grid">
              {ADDITIONAL_SERVICE_OPTIONS.map((s) => (
                <label key={s.key} className="lgrq-check lgrq-check--additional-svc">
                  <input
                    type="checkbox"
                    checked={Boolean(form.additionalServices[s.key])}
                    onChange={() => toggleAdditionalService(s.key)}
                  />
                  {s.label}
                </label>
              ))}
            </div>

            <h3 className="lgrq-svc-block-title lgrq-svc-block-title--spaced">Loading &amp; Unloading Requirements</h3>
            <div className="lgrq-loading-equip-row">
              <label className="lgrq-check lgrq-check--additional-svc">
                <input
                  type="checkbox"
                  checked={Boolean(form.loadingEquipmentRequired)}
                  onChange={(e) => setVal('loadingEquipmentRequired', e.target.checked)}
                />
                Loading Equipment Required
              </label>
              <label className="lgrq-check lgrq-check--additional-svc">
                <input
                  type="checkbox"
                  checked={Boolean(form.unloadingEquipmentRequired)}
                  onChange={(e) => setVal('unloadingEquipmentRequired', e.target.checked)}
                />
                Unloading Equipment Required
              </label>
            </div>
            <div className="lgrq-loading-card-row">
              <label className="lgrq-loading-option-card">
                <input
                  type="checkbox"
                  checked={Boolean(form.requiresLiftgate)}
                  onChange={(e) => setVal('requiresLiftgate', e.target.checked)}
                />
                <span>Tailgate Delivery (truck with liftgate)</span>
              </label>
              <label className="lgrq-loading-option-card">
                <input
                  type="checkbox"
                  checked={Boolean(form.appointmentDockRequired)}
                  onChange={(e) => setVal('appointmentDockRequired', e.target.checked)}
                />
                <span>Appointment Required (dock scheduling)</span>
              </label>
            </div>

            <h3 className="lgrq-svc-block-title lgrq-svc-block-title--spaced">Packaging Requirements</h3>
            <div className="lgrq-packaging-req-row">
              <label className="lgrq-check lgrq-check--additional-svc">
                <input
                  type="checkbox"
                  checked={Boolean(form.professionalPackagingRequired)}
                  onChange={(e) => setVal('professionalPackagingRequired', e.target.checked)}
                />
                Professional Packaging Required
              </label>
              <label className="lgrq-check lgrq-check--additional-svc">
                <input
                  type="checkbox"
                  checked={Boolean(form.cargoPalletized)}
                  onChange={(e) => setVal('cargoPalletized', e.target.checked)}
                />
                Cargo is Palletized
              </label>
            </div>
          </div>
        </section>

        <section className="lgrq-section lgrq-section--commercial">
          <header className="lgrq-section-head lgrq-section-head--commercial">
            <span className="lgrq-section-icon lgrq-section-icon--commercial-dollar" aria-hidden>
              <FaDollarSign />
            </span>
            <div className="lgrq-section-head-titles">
              <h2 className="lgrq-section-title lgrq-section-title--commercial">Commercial Terms &amp; Volume</h2>
              <p className="lgrq-section-subtitle lgrq-section-subtitle--commercial">
                Budget, payment terms, and shipment frequency
              </p>
            </div>
          </header>
          <div className="lgrq-section-body lgrq-section-body--commercial">
            <div className="lgrq-row lgrq-row--4">
              <Field label="Expected Budget (Optional)">
                <div className="lgrq-inline-value-unit">
                  <input
                    type="number"
                    min={0}
                    className="lgrq-input lgrq-input--grow"
                    value={form.expectedBudgetAmount}
                    onChange={(e) => setVal('expectedBudgetAmount', e.target.value)}
                    placeholder="50000"
                  />
                  <div className="lgrq-select-wrap lgrq-select-wrap--narrow">
                    <select
                      className="lgrq-select"
                      value={form.expectedBudgetCurrency}
                      onChange={(e) => setVal('expectedBudgetCurrency', e.target.value)}
                    >
                      {cargoCurrencySelectOptions.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </Field>
              <Field label="Quote Deadline" required>
                <div className="lgrq-input-wrap lgrq-input-wrap--date">
                  <input
                    type="date"
                    className="lgrq-input lgrq-input--date"
                    value={form.quoteDeadline}
                    onChange={(e) => setVal('quoteDeadline', e.target.value)}
                    placeholder="dd-mm-yyyy"
                    title="dd-mm-yyyy"
                  />
                  <span className="lgrq-input-suffix-icon" aria-hidden>
                    <FaCalendarAlt />
                  </span>
                </div>
              </Field>
              <Field label="Quote Validity Period">
                <div className="lgrq-select-wrap">
                  <select
                    className="lgrq-select"
                    value={form.quoteValidityPeriod}
                    onChange={(e) => setVal('quoteValidityPeriod', e.target.value)}
                  >
                    {QUOTE_VALIDITY_PERIOD_OPTIONS.map((t) => (
                      <option key={t || 'x'} value={t}>
                        {t || 'Select period'}
                      </option>
                    ))}
                  </select>
                </div>
              </Field>
              <Field label="Credit Terms">
                <input
                  className="lgrq-input"
                  value={form.creditTerms}
                  onChange={(e) => setVal('creditTerms', e.target.value)}
                  placeholder="e.g., Net 30"
                />
              </Field>
            </div>

            <hr className="lgrq-commercial-divider" />

            <h3 className="lgrq-commercial-block-title">Preferred Payment Terms</h3>
            <div className="lgrq-payment-terms-wrap">
              <Select
                isMulti
                className="lgrq-payment-terms-select"
                classNamePrefix="lgrq-pt"
                placeholder={paymentTermsLoading ? 'Loading…' : 'Select payment terms'}
                options={paymentTermSelectOptions}
                value={paymentTermsSelectValue}
                onChange={(opts) => setVal('preferredPaymentTerms', (opts || []).map((o) => o.value))}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                isClearable
                noOptionsMessage={() => 'No payment terms loaded'}
              />
            </div>

            <hr className="lgrq-commercial-divider" />

            <div className="lgrq-row lgrq-row--4">
              <Field label="Shipment Frequency">
                <div className="lgrq-select-wrap">
                  <select
                    className="lgrq-select"
                    value={form.shipmentFrequency}
                    onChange={(e) => setVal('shipmentFrequency', e.target.value)}
                  >
                    {COMMERCIAL_SHIPMENT_FREQUENCY_OPTIONS.map((t) => (
                      <option key={t || 'x'} value={t}>
                        {t || 'Select frequency'}
                      </option>
                    ))}
                  </select>
                </div>
              </Field>
              <Field label="Expected Volume per Month">
                <input
                  type="number"
                  min={0}
                  step={1}
                  className="lgrq-input"
                  value={form.expectedVolumePerMonth}
                  onChange={(e) => setVal('expectedVolumePerMonth', e.target.value)}
                  placeholder="e.g., 50"
                />
              </Field>
              <Field label="Annual Volume">
                <input
                  type="number"
                  min={0}
                  step={1}
                  className="lgrq-input"
                  value={form.annualVolume}
                  onChange={(e) => setVal('annualVolume', e.target.value)}
                  placeholder="e.g., 600"
                />
              </Field>
              <Field label="Contract Duration">
                <div className="lgrq-select-wrap">
                  <select
                    className="lgrq-select"
                    value={form.contractDuration}
                    onChange={(e) => setVal('contractDuration', e.target.value)}
                  >
                    {CONTRACT_DURATION_OPTIONS.map((t) => (
                      <option key={t || 'x'} value={t}>
                        {t || 'Select duration'}
                      </option>
                    ))}
                  </select>
                </div>
              </Field>
            </div>
          </div>
        </section>

        <section className="lgrq-section lgrq-section--carrier-doc">
          <header className="lgrq-section-head lgrq-section-head--carrier-doc">
            <span className="lgrq-section-icon lgrq-section-icon--carrier-doc" aria-hidden>
              <FaFileAlt />
            </span>
            <div className="lgrq-section-head-titles">
              <h2 className="lgrq-section-title lgrq-section-title--carrier-doc">Carrier Preferences &amp; Documentation</h2>
              <p className="lgrq-section-subtitle lgrq-section-subtitle--carrier-doc">
                Carrier requirements and supporting documents
              </p>
            </div>
          </header>
          <div className="lgrq-section-body lgrq-section-body--carrier-doc">
            <div className="lgrq-row lgrq-row--3">
              <Field label="Preferred Carriers" hint="Comma-separated list">
                <input
                  className="lgrq-input"
                  value={form.preferredCarriers}
                  onChange={(e) => setVal('preferredCarriers', e.target.value)}
                  placeholder="e.g., Maersk, DHL, FedEx"
                />
              </Field>
              <Field label="Excluded Carriers">
                <input
                  className="lgrq-input"
                  value={form.excludedCarriers}
                  onChange={(e) => setVal('excludedCarriers', e.target.value)}
                  placeholder="Carriers to avoid"
                />
              </Field>
              <Field label="Carrier Requirements">
                <input
                  className="lgrq-input"
                  value={form.carrierRequirements}
                  onChange={(e) => setVal('carrierRequirements', e.target.value)}
                  placeholder="e.g., ISO certified only"
                />
              </Field>
            </div>

            <hr className="lgrq-carrier-doc-divider" />

            <h3 className="lgrq-carrier-doc-block-title">Available Documents</h3>
            <div className="lgrq-available-docs-row">
              {[
                ['docCommercialInvoice', 'Commercial Invoice'],
                ['docPackingList', 'Packing List'],
                ['docMsds', 'MSDS'],
              ].map(([key, label]) => (
                <label key={key} className="lgrq-available-doc-pill">
                  <input
                    type="checkbox"
                    checked={Boolean(form[key])}
                    onChange={(e) => setVal(key, e.target.checked)}
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>

            <hr className="lgrq-carrier-doc-divider" />

            <div className="lgrq-carrier-upload-head">
              <h3 className="lgrq-carrier-upload-title">Upload Supporting Documents (Optional)</h3>
              <button type="button" className="lgrq-btn-add-outline" onClick={addDocument}>
                <span className="lgrq-btn-add-outline-plus" aria-hidden>
                  +
                </span>
                Add Document
              </button>
            </div>
            <div className="lgrq-carrier-doc-list">
              {form.documents.map((doc, i) => (
                <div key={doc.id} className="lgrq-carrier-doc-row">
                  <input type="file" className="lgrq-carrier-doc-file" onChange={(e) => onFileChange(i, e)} />
                  <div className="lgrq-carrier-doc-meta">
                    <span className="lgrq-carrier-doc-name">{doc.fileName || '—'}</span>
                    {doc.sizeLabel ? <span className="lgrq-carrier-doc-size">{doc.sizeLabel}</span> : null}
                  </div>
                  {form.documents.length > 1 ? (
                    <button type="button" className="lgrq-btn-remove" onClick={() => removeDocument(i)}>
                      Remove
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="lgrq-section lgrq-section--special-inst">
          <header className="lgrq-section-head lgrq-section-head--special-inst">
            <span className="lgrq-section-icon lgrq-section-icon--special-info" aria-hidden>
              <FaInfoCircle />
            </span>
            <div className="lgrq-section-head-titles">
              <h2 className="lgrq-section-title lgrq-section-title--special-inst">Special Instructions &amp; Additional Requirements</h2>
              <p className="lgrq-section-subtitle lgrq-section-subtitle--special-inst">
                Any specific requirements or preferences
              </p>
            </div>
          </header>
          <div className="lgrq-section-body lgrq-section-body--special-inst">
            <Field label="Special Instructions / Additional Comments">
              <textarea
                className="lgrq-textarea lgrq-textarea--special-main"
                value={form.specialInstructionsComments}
                onChange={(e) => setVal('specialInstructionsComments', e.target.value)}
                rows={6}
                placeholder="Any additional requirements, special handling instructions, or specific preferences for logistics providers..."
              />
            </Field>
            <div className="lgrq-row lgrq-row--2">
              <Field label="Compliance Requirements">
                <input
                  className="lgrq-input"
                  value={form.complianceRequirements}
                  onChange={(e) => setVal('complianceRequirements', e.target.value)}
                  placeholder="e.g., FDA approved, HACCP certified, etc."
                />
              </Field>
              <Field label="Sustainability Preferences">
                <input
                  className="lgrq-input"
                  value={form.sustainabilityPreferences}
                  onChange={(e) => setVal('sustainabilityPreferences', e.target.value)}
                  placeholder="e.g., Carbon-neutral shipping, eco-friendly packaging"
                />
              </Field>
            </div>

            <div className="lgrq-special-return-box">
              <span className="lgrq-special-return-icon" aria-hidden>
                📦
              </span>
              <div className="lgrq-special-return-body">
                <p className="lgrq-special-return-title">Return Logistics Required</p>
                <p className="lgrq-special-return-desc">Need reverse logistics services</p>
              </div>
              <label className="lgrq-switch lgrq-switch--accent">
                <input
                  type="checkbox"
                  checked={form.returnLogisticsRequired}
                  onChange={(e) => setVal('returnLogisticsRequired', e.target.checked)}
                />
                <span className="lgrq-switch-slider" />
              </label>
            </div>

            <div className="lgrq-special-terms-box">
              <input
                type="checkbox"
                className="lgrq-special-terms-check"
                id="lgrq-terms-accepted"
                checked={form.termsAccepted}
                onChange={(e) => setVal('termsAccepted', e.target.checked)}
              />
              <div className="lgrq-special-terms-copy">
                <label className="lgrq-special-terms-label" htmlFor="lgrq-terms-accepted">
                  I agree to the Terms &amp; Conditions
                  <span className="lgrq-required">*</span>
                </label>
                <p className="lgrq-special-terms-sub">
                  By submitting this RFQ, you agree to share your information with verified logistics providers and receive
                  quotes. Your data will be handled according to our privacy policy.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="lgrq-footer">
        <button
          type="button"
          className="lgrq-btn-draft"
          onClick={saveDraft}
          disabled={isSubmitting || isSavingDraft}
          aria-label="Save as draft"
        >
          {isSavingDraft ? 'Saving…' : 'Save as draft'}
        </button>
        <button
          type="button"
          className="lgrq-btn-submit-footer"
          onClick={submitRfq}
          disabled={isSubmitting}
          aria-label="Submit RFQ"
        >
          {isSubmitting ? 'Submitting…' : 'Submit RFQ'}
        </button>
      </footer>
    </div>
  );
}
